import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { usePreferences } from '../context/PreferencesContext';
import '../css/TemplateModal.css';
import { fetchPersonalInformation, updatePersonalInformation, fetchSavedTemplates, saveTemplate, deleteTemplate } from '../utils/PersonalInformationUtils';

const TEMPLATE_MAX_LENGTH = 10000;

function TemplateModal({ isOpen, onClose, onTemplateSelect }) {
  const { t, i18n } = useTranslation();
  const { retry: refreshPreferences } = usePreferences();
  const [doctorInfo, setDoctorInfo] = useState('');
  const [practiceInfo, setPracticeInfo] = useState('');
  const [templateInfo, setTemplateInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [infoLoaded, setInfoLoaded] = useState(false);
  const [pendingAction, setPendingAction] = useState('');
  const [error, setError] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [isStoringTemplate, setIsStoringTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const busy = Boolean(pendingAction);
  const selectedTemplate = savedTemplates.find(template => String(template.id) === selectedTemplateId);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setInfoLoaded(false);
    setError(null);
    setIsStoringTemplate(false);
    setNewTemplateName('');
    setTemplateToDelete(null);
    setSelectedTemplateId('');
    setSavedTemplates([]);
    Promise.allSettled([fetchPersonalInformation(), fetchSavedTemplates()]).then(([infoResult, templatesResult]) => {
      if (cancelled) return;
      const templates = templatesResult.status === 'fulfilled'
        ? [...(templatesResult.value.templates || []), ...(templatesResult.value.default_templates || [])]
        : [];
      setSavedTemplates(templates);
      if (infoResult.status === 'fulfilled') {
        const data = infoResult.value;
        const currentTemplate = data.template_info || '';
        setDoctorInfo(data.user_info || '');
        setPracticeInfo(data.practice_info || '');
        setTemplateInfo(currentTemplate);
        const match = templates.find(template => template.template_data === currentTemplate);
        setSelectedTemplateId(match ? String(match.id) : currentTemplate.trim() ? 'custom' : 'empty');
        setInfoLoaded(true);
        if (templatesResult.status === 'rejected') setError(t('templates.loadError'));
      } else {
        setError(t('templates.loadInfoError'));
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [isOpen, t]);

  const handleTemplateSelect = event => {
    const id = event.target.value;
    const template = savedTemplates.find(item => String(item.id) === id);
    if (id === 'empty' || template) {
      setSelectedTemplateId(id);
      setTemplateInfo(template ? template.template_data : '');
      setError(null);
    }
  };

  const handleTemplateEdit = event => {
    const value = event.target.value;
    const match = savedTemplates.find(template => template.template_data === value);
    setTemplateInfo(value);
    setSelectedTemplateId(match ? String(match.id) : value.trim() ? 'custom' : 'empty');
  };

  const handleStoreTemplate = async event => {
    event.preventDefault();
    if (busy) return;
    if (templateInfo.length > TEMPLATE_MAX_LENGTH) {
      setError(t('templates.tooLong'));
      return;
    }
    const name = newTemplateName.trim();
    if (!name) {
      setError(t('templates.nameRequired'));
      return;
    }
    setPendingAction('store');
    setError(null);
    let stored = false;
    try {
      await saveTemplate(name, templateInfo);
      stored = true;
      setIsStoringTemplate(false);
      setNewTemplateName('');
      const data = await fetchSavedTemplates();
      const templates = [...(data.templates || []), ...(data.default_templates || [])];
      setSavedTemplates(templates);
      const match = (data.templates || []).find(template => template.name === name && template.template_data === templateInfo);
      if (match) setSelectedTemplateId(String(match.id));
    } catch (err) {
      setError(t(stored ? 'templates.loadError' : 'templates.saveError'));
    } finally {
      setPendingAction('');
    }
  };

  const handleDeleteTemplate = async () => {
    if (busy || !templateToDelete) return;
    setPendingAction('delete');
    setError(null);
    try {
      await deleteTemplate(templateToDelete.id);
      setSavedTemplates(templates => templates.filter(template => String(template.id) !== String(templateToDelete.id)));
      if (selectedTemplateId === String(templateToDelete.id)) {
        setSelectedTemplateId(templateInfo.trim() ? 'custom' : 'empty');
      }
      setTemplateToDelete(null);
    } catch (err) {
      setError(t('templates.deleteError'));
    } finally {
      setPendingAction('');
    }
  };

  const handleSave = async () => {
    if (busy || loading || !infoLoaded) return;
    if (templateInfo.length > TEMPLATE_MAX_LENGTH) {
      setError(t('templates.tooLong'));
      return;
    }
    setPendingAction('save');
    setError(null);
    try {
      await updatePersonalInformation(doctorInfo, practiceInfo, templateInfo);
      refreshPreferences();

      if (onTemplateSelect) {
        onTemplateSelect(templateInfo.trim() ? selectedTemplate || { custom: true, template_data: templateInfo } : null);
      }

      onClose();
    } catch (err) {
      setError(t('templates.saveInfoError'));
    } finally {
      setPendingAction('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="template-modal-overlay">
      <div className="template-modal-content">
        <h2>{t('templates.title')}</h2>

        {error && <p role="alert" className="error-message">{error}</p>}

        <div className="template-input-group">
          <div className="doctor-info">
            <label className="template-label" htmlFor="doctor-info-input">{t('templates.doctorInfo')}</label>
            <textarea dir="auto"
              id="doctor-info-input"
              className="template-textarea doctor-info-textarea"
              value={doctorInfo}
              disabled={loading || busy || !infoLoaded}
              onChange={(e) => setDoctorInfo(e.target.value)}
              placeholder={t('templates.doctorPlaceholder')}
              maxLength={500}
            />
          </div>

          <div className="practice-info">
            <label className="template-label" htmlFor="practice-info-input">{t('templates.practiceInfo')}</label>
            <textarea dir="auto"
              id="practice-info-input"
              className="template-textarea practice-info-textarea"
              value={practiceInfo}
              disabled={loading || busy || !infoLoaded}
              onChange={(e) => setPracticeInfo(e.target.value)}
              placeholder={t('templates.practicePlaceholder')}
              maxLength={500}
            />
          </div>

          <div className="template-info">
            <label className="template-label" htmlFor="template-info-input">{t('templates.templateInfo')}</label>

            <textarea dir="auto"
              id="template-info-input"
              className="template-textarea template-info-textarea"
              value={templateInfo}
              onChange={handleTemplateEdit}
              disabled={loading || busy || !infoLoaded}
              placeholder={t('templates.contentPlaceholder')}
              maxLength={TEMPLATE_MAX_LENGTH}
              aria-describedby="template-character-count"
              aria-invalid={templateInfo.length > TEMPLATE_MAX_LENGTH}
            />

            <p id="template-character-count" className="template-character-count">
              {t('templates.characterCount', { used: templateInfo.length.toLocaleString(i18n.resolvedLanguage), limit: TEMPLATE_MAX_LENGTH.toLocaleString(i18n.resolvedLanguage) })}
            </p>

            <div className="template-controls">
              <select
                id="template-select"
                aria-label={t('templates.select')}
                className="template-select"
                value={selectedTemplateId}
                onChange={handleTemplateSelect}
                disabled={loading || busy || !infoLoaded || isStoringTemplate || Boolean(templateToDelete)}
              >
                <option value="" disabled>{t('templates.select')}</option>
                <option value="empty">{t('templates.empty')}</option>
                {selectedTemplateId === 'custom' && <option value="custom" disabled>{t('templates.custom')}</option>}
                {savedTemplates.map(template => <option key={template.id} value={String(template.id)}>{template.name}</option>)}
              </select>
              <button
                type="button"
                className="store-template-button"
                disabled={loading || busy || !infoLoaded || !templateInfo.trim() || isStoringTemplate || Boolean(templateToDelete)}
                onClick={() => { setError(null); setIsStoringTemplate(true); }}
              >{t('templates.saveAs')}</button>
              {selectedTemplate && !String(selectedTemplate.id).startsWith('default-') && (
                <button
                  type="button"
                  className="delete-template-button"
                  disabled={loading || busy || isStoringTemplate || Boolean(templateToDelete)}
                  onClick={() => { setError(null); setTemplateToDelete(selectedTemplate); }}
                >{t('templates.delete')}</button>
              )}
            </div>

            {isStoringTemplate && (
              <form className="store-template-modal" onSubmit={handleStoreTemplate}>
                <input
                  type="text"
                  dir="auto"
                  aria-label={t('templates.namePlaceholder')}
                  placeholder={t('templates.namePlaceholder')}
                  maxLength={50}
                  value={newTemplateName}
                  onChange={event => setNewTemplateName(event.target.value)}
                  disabled={busy}
                  autoFocus
                />
                <div className="store-template-buttons">
                  <button type="submit" disabled={busy}>{pendingAction === 'store' ? t('common.saving') : t('common.save')}</button>
                  <button type="button" disabled={busy} onClick={() => { setIsStoringTemplate(false); setNewTemplateName(''); setError(null); }}>{t('common.cancel')}</button>
                </div>
              </form>
            )}

            {templateToDelete && (
              <div className="store-template-modal">
                <p>{t('templates.confirmDelete')}</p>
                <div className="store-template-buttons">
                  <button type="button" disabled={busy} onClick={handleDeleteTemplate}>{t('templates.yesDelete')}</button>
                  <button type="button" disabled={busy} onClick={() => { setTemplateToDelete(null); setError(null); }}>{t('common.cancel')}</button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="template-modal-buttons">
          <button className="template-save-button" onClick={handleSave} disabled={loading || busy || !infoLoaded || isStoringTemplate || Boolean(templateToDelete)}>
            {pendingAction === 'save' ? t('common.saving') : t('common.save')}
          </button>
          <button className="template-close-button" onClick={onClose} disabled={busy}>{t('common.close')}</button>
        </div>
      </div>
    </div>
  );
}

export default TemplateModal;
